import { Translate } from '@chipinside/translate'

import { FormProps, UseFormsProps } from '#/store/hooks/useForms/types'
import { endpoint } from '#/store/services/endpoints'

type WithCompany = {
  company: string
}
const companyForms = {
  company: {
    delete: ({
      params,
    }: FormProps<
      WithCompany & {
        companyName: string
      }
    >): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.companyName as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'company' }) as string,
            },
          }),
        },
      }),
      save: {
        controller: endpoint.company.remove(params),
        method: 'delete',
      },
      subtitle: params?.companyName,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'company' }) as string,
        },
      }),
    }),
    register: (): UseFormsProps => ({
      controller: endpoint.company.register_form(),
      save: {
        controller: endpoint.company.register(),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'company' }),
        },
      }),
      width: 'md',
    }),
    update: ({ params }: FormProps<WithCompany>): UseFormsProps => ({
      controller: endpoint.company.update_form(params),
      save: {
        controller: endpoint.company.update(params),
        method: 'put',
      },
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'company' }),
        },
      }),
      width: 'md',
    }),
  },

  company_okr: {
    update: ({
      params,
    }: FormProps<WithCompany & { metric: string; metricName?: string }>): UseFormsProps => ({
      controller: endpoint.company.okr.update_form(params),

      save: {
        controller: endpoint.company.okr.update(params),
        method: 'put',
      },
      title: Translate({
        messageKey: 'update_item',
        params: { item: params?.metricName! },
      }),
      width: 'lg',
    }),
  },

  company_portfolio: {
    delete: ({
      params,
    }: FormProps<
      WithCompany & {
        companyPortfolioId: string
        companyPortfolioName: string
      }
    >): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.companyPortfolioName as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'portfolio' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.company.portfolio.delete({
          company: params?.company,
          portfolio: params?.companyPortfolioId,
        }),
        method: 'delete',
      },
      subtitle: params?.companyPortfolioName,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'portfolio' }),
        },
      }),
    }),

    register: ({ params }: FormProps<WithCompany>): UseFormsProps => ({
      controller: endpoint.company.portfolio.register_form(params),
      save: {
        controller: endpoint.company.portfolio.register(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'portfolio' }),
        },
      }),
      width: 'xs',
    }),

    update: ({
      params,
    }: FormProps<
      WithCompany & {
        companyPortfolioId: string
        companyPortfolioName: string
      }
    >): UseFormsProps => ({
      controller: endpoint.company.portfolio.update_form({
        company: params?.company,
        portfolio: params?.companyPortfolioId,
      }),
      save: {
        controller: endpoint.company.portfolio.update({
          company: params?.company,
          portfolio: params?.companyPortfolioId,
        }),
        method: 'put',
      },
      subtitle: params?.companyPortfolioName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'portfolio' }),
        },
      }),
      width: 'xs',
    }),
  },

  company_projects: {
    delete: ({
      params,
    }: FormProps<
      WithCompany & { company_project: string; company_project_name: string }
    >): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.company_project_name as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'project' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.company.projects.delete(params),
        method: 'delete',
      },
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'project' }),
        },
      }),
      width: 'sm',
    }),

    register: ({ params }: FormProps<WithCompany>): UseFormsProps => ({
      controller: endpoint.company.projects.register_form(params),
      save: {
        controller: endpoint.company.projects.register(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'project' }),
        },
      }),
      width: 'md',
    }),
    update: ({
      params,
    }: FormProps<WithCompany & { company_project: string }>): UseFormsProps => ({
      controller: endpoint.company.projects.update_form(params),
      save: {
        controller: endpoint.company.projects.update(params),
        method: 'put',
      },
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'project' }),
        },
      }),
      width: 'md',
    }),
  },

  company_roles: {
    delete: ({
      params,
    }: FormProps<WithCompany & { role: string; roleName: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.roleName as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'role' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.company.role.self(params),
        method: 'delete',
      },
      subtitle: params?.roleName,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'role' }) as string,
        },
      }),
    }),
    duplicate: ({ params }: FormProps<WithCompany & { role: string }>): UseFormsProps => ({
      controller: endpoint.company.role.register_form(params),
      save: {
        controller: endpoint.company.role.duplicate(params),
        manipulateParams(values) {
          return params?.role ? { ...values, role_slug: params?.role } : values
        },
        method: 'post',
      },
      title: Translate({
        messageKey: 'duplicate_item',
        params: {
          item: Translate({ lower: true, messageKey: 'role' }),
        },
      }),
      width: 'xs',
    }),
    register: ({ params }: FormProps<WithCompany>): UseFormsProps => ({
      controller: endpoint.company.role.register_form(params),
      save: {
        controller: endpoint.company.role.register(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'role' }),
        },
      }),
      width: 'xs',
    }),

    update: ({ params }: FormProps<WithCompany & { role: string }>): UseFormsProps => ({
      controller: endpoint.company.role.update_form(params),
      save: {
        controller: endpoint.company.role.self(params),
        method: 'put',
      },
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'role' }),
        },
      }),
      width: 'xs',
    }),
  },

  company_users: {
    delete: ({
      params,
    }: FormProps<WithCompany & { user: string; userName: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.userName as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'user' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.company.user.remove(params),
        method: 'delete',
      },
      subtitle: params?.userName,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'user' }),
        },
      }),
    }),
    register: ({ params }: FormProps<WithCompany>): UseFormsProps => ({
      controller: endpoint.company.user.register_form(params),
      save: {
        controller: endpoint.company.user.register(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'add_item',
        params: {
          item: Translate({ lower: true, messageKey: 'user' }),
        },
      }),
    }),

    update: ({ params }: FormProps<WithCompany & { user: string }>): UseFormsProps => ({
      controller: endpoint.company.user.update_form(params),
      save: {
        controller: endpoint.company.user.update(params),
        method: 'put',
      },
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'user' }),
        },
      }),
    }),
  },

  customization: {
    register: ({ params }: FormProps<WithCompany>): UseFormsProps => ({
      controller: endpoint.company.customization.form(params),
      save: {
        controller: endpoint.company.customization.register(params),
        method: 'post',
      },
    }),
  },
}

export { companyForms }
